import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FilterFilled, RedoOutlined, SearchOutlined } from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Pagination,
  Space,
  Table,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { getIsRefresh, setIsRefresh } from "stores/slices/layout";

import AppButton from "../button";

const { RangePicker } = DatePicker;

const AppTable = props => {
  const {
    api,
    columns,
    searchParam,
    handleSearch,
    handleFilter,
    handleTableChange,
    scroll,
    setSearchParam,
    pageSizeOptions,
    onCustomFetchData = null,
    border = false,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isRefresh = useSelector(getIsRefresh);

  const rangePresets = [
    {
      label: t("rangePresets.last7Days"),
      value: [dayjs().add(-7, "d"), dayjs()],
    },
    {
      label: t("rangePresets.last14Days"),
      value: [dayjs().add(-14, "d"), dayjs()],
    },
    {
      label: t("rangePresets.last30Days"),
      value: [dayjs().add(-30, "d"), dayjs()],
    },
    {
      label: t("rangePresets.last90Days"),
      value: [dayjs().add(-90, "d"), dayjs()],
    },
  ];
  const searchInput = useRef(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  // const [inputPricingValue, setInputPricingValue] = useState([]);

  const [col, setCol] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    if (api) {
      const { payload } = await dispatch(api);
      return payload;
    }
  };

  const handleReset = (setSelectedKeys, dataIndex, clearFilters, confirm) => {
    clearFilters();
    setSelectedKeys([]);
    handleSearch([], confirm, dataIndex);
  };

  useEffect(() => {
    let columnsHandle = columns.map(column => {
      if (column.isSearchable) {
        column = {
          ...column,
          ...getColumnSearchProps(column.dataIndex, column.searchPlaceholder),
        };
      }
      if (column.isFilterable) {
        column = { ...column, ...getColumnFilterProps(column.dataIndex) };
      }
      if (column.isShowPricingRange) {
        column = { ...column, ...getColumnPricingRangeProps(column.dataIndex) };
      }
      return column;
    });
    setCol(columnsHandle);
  }, [columns]);

  useEffect(() => {
    if (onCustomFetchData) {
      setLoading(true);
      onCustomFetchData().then(resp => {
        console.log("on fetch", resp);
        if (resp && resp.pageNumber && resp.pageSize && resp.totalElements) {
          setPagination({
            current: resp.pageNumber,
            pageSize: resp.pageSize,
            total: resp.totalElements,
          });
          setData(resp.content, { deep: true });
        } else {
          setData(resp?.content, { deep: true });
        }
        setLoading(false);
      });
    } else {
      fetchData().then(resp => {
        console.log("fetchData", resp);
        if (resp && resp.pageNumber && resp.pageSize && resp.totalElements) {
          setPagination({
            current: resp.pageNumber,
            pageSize: resp.pageSize,
            total: resp.totalElements,
          });
          setData(resp.content, { deep: true });
        } else {
          setData(resp?.content, { deep: true });
        }
        setLoading(false);
      });
    }
  }, [searchParam, isRefresh]);

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={e => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={placeholder ? placeholder : `Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <AppButton
            label={t("button.search")}
            color={"primary"}
            size="small"
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            style={tableStyles.buttonAdd}
          />
          <AppButton
            label={t("button.reset")}
            color={"primary"}
            size="small"
            varient={"outlined"}
            onClick={() =>
              clearFilters &&
              handleReset(setSelectedKeys, dataIndex, clearFilters, confirm)
            }
            style={tableStyles.buttonAdd}
          />
          <AppButton
            label={t("button.close")}
            color={"primary"}
            type="link"
            size="small"
            varient={"outlined"}
            onClick={() => close()}
            style={tableStyles.buttonAdd}
          />
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const getColumnFilterProps = dataIndex => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={e => e.stopPropagation()}
      >
        <RangePicker
          presets={rangePresets}
          onChange={dates => {
            setSelectedKeys(dates ? [...dates] : []);
          }}
          value={selectedKeys}
          style={{
            marginBottom: 8,
            display: "flex",
          }}
        />
        <Space>
          <AppButton
            label={t("button.filter")}
            color={"primary"}
            size="small"
            type="primary"
            onClick={() => handleFilter(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            style={tableStyles.buttonAdd}
          />
          <AppButton
            label={t("button.reset")}
            color={"primary"}
            size="small"
            varient={"outlined"}
            onClick={() => {
              clearFilters();
              handleSearch(selectedKeys, confirm, dataIndex);
            }}
            style={tableStyles.buttonAdd}
          />
          <AppButton
            label={t("button.close")}
            color={"primary"}
            type="link"
            size="small"
            varient={"outlined"}
            onClick={() => close()}
            style={tableStyles.buttonAdd}
          />
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <FilterFilled
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
  });

  const getColumnPricingRangeProps = dataIndex => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={e => e.stopPropagation()}
      >
        <>
          <div style={{ marginBottom: "8px" }}>
            <InputNumber
              min={0}
              max={selectedKeys[1]}
              controls={false}
              style={{
                margin: "0 8px 0 0",
                width: "120px",
              }}
              formatter={value =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={value => value.replace(/\$\s?|(,*)/g, "")}
              defaultValue={selectedKeys[0]}
              value={selectedKeys[0]}
              onChange={newValue => {
                setSelectedKeys([newValue, selectedKeys[1]]);
              }}
            />
            {" - "}
            <InputNumber
              min={selectedKeys[0]}
              max={999999999}
              controls={false}
              style={{
                margin: "0 0 0 8px",
                width: "120px",
              }}
              formatter={value =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={value => value.replace(/\$\s?|(,*)/g, "")}
              defaultValue={selectedKeys[1]}
              value={selectedKeys[1]}
              onChange={newValue1 => {
                setSelectedKeys([selectedKeys[0], newValue1]);
              }}
            />
          </div>
        </>
        <Space>
          <AppButton
            label={t("button.filter")}
            color={"primary"}
            size="small"
            type="primary"
            onClick={() => handleFilter(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            style={tableStyles.buttonAdd}
          />
          <AppButton
            label={t("button.reset")}
            color={"primary"}
            size="small"
            varient={"outlined"}
            onClick={() => {
              clearFilters();
              handleSearch(selectedKeys, confirm, dataIndex);
            }}
            style={tableStyles.buttonAdd}
          />
          <AppButton
            label={t("button.close")}
            color={"primary"}
            type="link"
            size="small"
            varient={"outlined"}
            onClick={() => close()}
            style={tableStyles.buttonAdd}
          />
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <FilterFilled
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
  });

  return (
    <>
      <Table
        columns={col}
        rowKey={() => uuid()}
        dataSource={data}
        pagination={false}
        loading={loading}
        onChange={handleTableChange}
        scroll={scroll ? scroll : { x: 1200 }}
        bordered={border}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "12px",
        }}
      >
        <Tooltip title={t("button.refresh")}>
          <Button type="text" onClick={() => dispatch(setIsRefresh(uuid()))}>
            <RedoOutlined />
          </Button>
        </Tooltip>

        <Pagination
          defaultCurrent={1}
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page, pageSize) => {
            const newSearchParam = {
              ...searchParam,
              pageNumber: page,
              pageSize: pageSize,
            };
            setSearchParam(newSearchParam);
          }}
          showSizeChanger={true}
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </>
  );
};

const tableStyles = {
  mainContainer: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    // gap: "24px",
    marginBottom: "24px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  buttonAdd: {
    minWidth: "auto",
  },
};

export default AppTable;
